import { useState } from "react";
import type { ReorderSuggestion } from "@dentacore/shared-types";

import { createSupplierOrder, receiveSupplierOrder } from "../api/suppliers";
import { formatMoney } from "../patients/money";
import { useSeesPerUseCost } from "./use-sees-per-use-cost";
import { OrderDialog } from "./order-dialog";
import { ReceiveDialog } from "./receive-dialog";
import { expectedText, leadText, namesText, perUseText } from "./supplier-text";
import inv from "./inventory.module.css";
import styles from "./suppliers.module.css";

/** One line saying who to buy from — the recommendation itself (SU-D1, SU-D5). */
export function recommendationText(
  s: ReorderSuggestion,
  currency: string,
  /** Price a use is a cost figure: shown only with Accounting (2026-10-16). */
  perUse: boolean,
): string {
  const cheapest = s.prices.filter((p) => s.cheapestIds.includes(p.id));
  const first = cheapest[0];
  if (s.openOrder !== null) {
    if (s.openOrder.sentAt === null) {
      return `On order from ${s.openOrder.supplierName}, not sent yet — copy the message from Order history.`;
    }
    const when = expectedText(s.openOrder.sentAt, s.openOrder.leadDays);
    return `On order from ${s.openOrder.supplierName}${when === null ? "" : `, expected ${when}`}.`;
  }
  if (first === undefined) return "No supplier sells this yet.";
  if (cheapest.length > 1) {
    return `${namesText(cheapest.map((p) => p.supplierName))} are the same price${perUse ? ` (${perUseText(first, currency)} a use)` : ""} — pick either.`;
  }
  const lead = leadText(first.leadDays);
  return (
    `Order ${String(first.suggestedPacks)} × ${first.pack} from ${first.supplierName} — ` +
    `${formatMoney(first.priceCents * first.suggestedPacks, currency)}, the cheapest` +
    `${perUse ? ` at ${perUseText(first, currency)} a use` : ""}${lead === null ? "" : `, ${lead}`}.`
  );
}

/**
 * What a low-stock notice offers, wherever it is drawn — Home's alert and the
 * popup (SU-D3). *Order it* opens the order dialog; once ordered the notice goes
 * quiet and offers *It arrived* instead. It carries its own dialogs, so a
 * notice can act without the inventory screen being open.
 */
export function ReorderActions({
  suggestion,
  currency,
  canOrder,
  onChanged,
  onError,
  compact = false,
}: {
  suggestion: ReorderSuggestion;
  currency: string;
  canOrder: boolean;
  /** Something was ordered or received: re-read the notices. */
  onChanged: () => void;
  onError: (message: string) => void;
  /** The popup's form: no recommendation line, which it draws itself. */
  compact?: boolean;
}): React.JSX.Element {
  // Price per use needs Accounting and accounting.view (QF-D20).
  const hasAccounting = useSeesPerUseCost();
  const [ordering, setOrdering] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [busy, setBusy] = useState(false);

  const attempt = async (write: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    try {
      await write();
      onChanged();
      return true;
    } catch (cause: unknown) {
      onError(cause instanceof Error ? cause.message : "That did not go through.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const order = suggestion.openOrder;
  const phones = new Map(suggestion.prices.map((p) => [p.supplierId, p.supplierPhone]));

  return (
    <>
      {!compact && (
        <span className={styles.rec} data-testid="reorder-rec">
          {recommendationText(suggestion, currency, hasAccounting)}
        </span>
      )}
      {canOrder && order === null && suggestion.prices.length > 0 && (
        <button
          type="button"
          className={inv.primary}
          disabled={busy}
          onClick={() => setOrdering(true)}
          data-testid="reorder-order"
        >
          Order it
        </button>
      )}
      {canOrder && order !== null && (
        <button
          type="button"
          className={inv.ghost}
          disabled={busy}
          onClick={() => setReceiving(true)}
        >
          It arrived
        </button>
      )}

      {ordering && (
        <OrderDialog
          item={{
            id: suggestion.itemId,
            name: suggestion.itemName,
            quantityOnHand: suggestion.quantityOnHand,
            parLevel: suggestion.fullShelf,
            usesPerStockUnit: suggestion.fullShelf,
          }}
          prices={suggestion.prices}
          phones={phones}
          currency={currency}
          busy={busy}
          onClose={() => setOrdering(false)}
          onOrder={(body) => attempt(() => createSupplierOrder(body))}
        />
      )}
      {receiving && order !== null && (
        <ReceiveDialog
          order={order}
          quantityOnHand={suggestion.quantityOnHand}
          currency={currency}
          busy={busy}
          onClose={() => setReceiving(false)}
          onReceive={(body) => attempt(() => receiveSupplierOrder(order.id, body))}
        />
      )}
    </>
  );
}
